import { SocketClient } from "cbcore-ts"
import { UIView, UIViewController } from "uicore-ts"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHPanelView } from "../Custom components/RH/RHPanelView"
import { RHTextField } from "../Custom components/RH/RHTextField"


export class AsyncGuardTestViewController extends UIViewController {
    
    
    _panelView = new RHPanelView().addedAsSubviewToView(this.view)
    
    _titleLabel = new UIView().addedAsSubviewToView(this._panelView).configuredWithObject({
        innerHTML: "Async guard test",
        style: {
            fontSize: "1.125rem",
            fontWeight: "600",
            color: RHColor.textPrimary.stringValue
        }
    })
    
    _delayTextField = new RHTextField().addedAsSubviewToView(this._panelView).configuredWithObject({
        placeholderText: "Delay in seconds (default 2)",
        type: "number"
    })
    
    _guardedButton = new RHButton().addedAsSubviewToView(this._panelView).configuredWithObject({
        titleLabel: { text: "Guarded (default)" }
    })
    
    _unguardedButton = new RHButton().addedAsSubviewToView(this._panelView).configuredWithObject({
        titleLabel: { text: "Unguarded (allowConcurrentCalls)" }
    })
    
    _logView = new UIView().addedAsSubviewToView(this._panelView).configuredWithObject({
        style: {
            overflowY: "auto",
            fontFamily: "monospace",
            fontSize: "0.8rem",
            backgroundColor: RHColor.surface.stringValue,
            border: "1px solid " + RHColor.border.stringValue,
            borderRadius: "6px",
            padding: "8px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all"
        }
    })
    
    _clearButton = new RHButton().addedAsSubviewToView(this._panelView).configuredWithObject({
        titleLabel: { text: "Tühjenda logi" }
    })
    
    _callCount = 0
    _logLines: string[] = []
    
    
    constructor() {
        
        super(new UIView())
        
        this._guardedButton.setVariant("primary")
        this._unguardedButton.setVariant("secondary")
        this._clearButton.setVariant("light")
        
        this._guardedButton.addTargetForControlEvent(
            UIView.controlEvent.PointerUpInside,
            async (sender, event) => {
                await this._performTestCall("Guarded")
            }
        )
        
        this._unguardedButton.addTargetForControlEvent(
            UIView.controlEvent.PointerUpInside,
            async (sender, event, callbackControl) => {
                callbackControl.allowConcurrentCalls()
                await this._performTestCall("Unguarded")
            }
        )
        
        // noinspection JSConstantReassignment
        this._clearButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._logLines = []
            this._refreshLog()
        }
        
    }
    
    
    static override readonly routeComponentName = "async_guard_test"
    static override readonly ParameterIdentifierName = {}
    
    
    async _performTestCall(label: string) {
        
        this._callCount++
        const callIndex = this._callCount
        const delay = Number(this._delayTextField.text) || 2
        
        this._log(`[${label}] Kõne ${callIndex} alustatud (viivitus ${delay}s)`)
        
        try {
            
            const result = await SocketClient.PerformActionAndRetrieveDataForTesting({
                returnData: `call_${callIndex}_${label}`,
                returnDelayInSeconds: delay
            })
            
            this._log(`[${label}] Kõne ${callIndex} lõpetatud → ${JSON.stringify(result)}`)
            
        }
        catch (error) {
            
            this._log(`[${label}] Kõne ${callIndex} viga: ${error}`)
            
        }
        
    }
    
    
    _log(line: string) {
        const timestamp = new Date().toISOString().substring(11, 23)
        this._logLines.push(`${timestamp}  ${line}`)
        this._refreshLog()
    }
    
    
    _refreshLog() {
        this._logView.innerHTML = this._logLines.join("\n") || "(logi tühi)"
        this._logView.viewHTMLElement.scrollTop = this._logView.viewHTMLElement.scrollHeight
    }
    
    
    override layoutViewSubviews() {
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.view.contentBoundsWithInset(padding)
        
        this._panelView.frame = bounds
        
        const panelBounds = this._panelView.contentBoundsWithInset(padding * 1.5)
        
        panelBounds
            .rectangleWithHeight(labelHeight)
            .assignedAsFrameOfView(this._titleLabel)
            
            .rectangleForNextRow(padding, labelHeight * 1.5)
            .assignedAsFrameOfView(this._delayTextField)
            
            .rectangleForNextRow(padding, labelHeight * 1.5)
            .distributeViewsEquallyAlongWidth(
                [this._guardedButton, this._unguardedButton],
                padding
            )
            
            .rectangleForNextRow(padding, 300)
            .assignedAsFrameOfView(this._logView)
            
            .rectangleForNextRow(padding, labelHeight * 1.5)
            .rectangleWithWidth(padding * 10, 1)
            .assignedAsFrameOfView(this._clearButton)
        
    }
    
    
}
